/**
 * useCompetitionJudging — recording a climb, and taking it back.
 *
 * The competitor's own JAM save, the judge's per-event recording, and the undo
 * for each. Both write paths go through the durable queue rather than straight
 * to Firestore: two judges on a congested hall network used to race and
 * overwrite each other, so the aggregate `attempts/{uid}_{routeId}` document
 * is rebuilt server-side from the audit subcollection and never written here.
 */
import { useCallback } from "react";
import { doc, setDoc, serverTimestamp } from "firebase/firestore";
import { db } from "../../firebase";
import { isAttemptRecordingLocked } from "../../competitionUtils";
import { trackMAU } from "../useMAU";
import { enqueueJudgeEvent, enqueueJudgeRevoke, enqueueJamSave, removePendingEvent, removePendingJamSave, buildEventId } from "../../utils/judgeQueue";
import { withTimeout, WRITE_TIMEOUT_MS } from "../../utils/promiseTimeout";

export function useCompetitionJudging({ gymId, authUser, competitions, sessions }) {

  const saveAttempt = useCallback(async (compId, uid, routeId, data, opts = {}) => {
    if (!gymId) return;
    const attemptId = `${uid}_${routeId}`;
    const actorUid = opts.actorUid || authUser?.uid || uid;
    const actorRole = opts.actorRole || "competitor";

    // Attempt-recording lock: a comp-wide or per-session lock blocks new
    // entry. gymAdmin/organizer explicit corrections (admin_edit / import)
    // still pass — the lock freezes live judging/JAM, not admin fixes.
    const isAdminActor = actorRole === "admin" || actorRole === "organizer";
    if (!isAdminActor) {
      const comp = competitions.find(c => c.id === compId);
      const session = data.sessionId ? sessions.find(s => s.id === data.sessionId) : null;
      if (isAttemptRecordingLocked(comp, session)) {
        const err = new Error("attempts-locked");
        err.code = "attempts-locked";
        throw err;
      }
    }

    const snapshot = {
      zoneAttempts: data.zoneAttempts || 0,
      zoneReached: data.zoneReached || false,
      topAttempts: data.topAttempts || 0,
      topped: data.topped || false,
    };

    // Competitor JAM self-save — goes through the durable queue (same
    // resilience guarantees as judge events). The queue writes both the
    // aggregate doc and the `jam_save` audit entry idempotently; offline
    // saves survive tab refresh / PWA kill and flush on reconnect.
    const isCompetitorSelfSave = actorRole === "competitor"
      && actorUid === uid
      && actorUid === authUser?.uid
      && !opts.auditType; // don't intercept callers that override audit type
    if (isCompetitorSelfSave) {
      // Return the queue ID so the modal can offer an undo on the post-save
      // toast (drop the pending item, or re-save the prior snapshot if it
      // already flushed — see undoJamSave).
      const queueId = await enqueueJamSave({
        gymId,
        compId,
        attemptId,
        uid,
        routeId,
        groupId: data.groupId || "",
        sessionId: data.sessionId || "",
        actorUid,
        actorRole,
        snapshot,
        beforeSnapshot: opts.beforeSnapshot || null,
      });
      trackMAU(gymId, uid).catch(() => {});
      return queueId;
    }

    // Admin / organizer / bulk-import path — direct Firestore writes rather
    // than the durable queue, because delegation semantics ("judge B uploads
    // judge A's taps") make no sense for an admin correction: the organizer
    // fixing a score wants to know NOW whether it landed.
    //
    // That makes the deadline mandatory. Firestore is memory-only here, so a
    // stalled write never rejects — the organizer's save button would sit on
    // "Saving…" forever on congested gym wifi, and a reload would drop the
    // write silently. Both writes below therefore carry a timeout, and both
    // are idempotent so a retry after one converges instead of duplicating.
    const auditType = opts.auditType
      || ((actorRole === "admin" || actorRole === "organizer") ? "admin_edit" : "jam_save");
    const beforeSnapshot = opts.beforeSnapshot || null;
    // Explicit audit id instead of `addDoc`'s fresh one. A caller that can
    // replay a logical operation — the Excel import re-run after a stalled row
    // is the case that bites — passes a stable id, so the replay overwrites its
    // own entry instead of leaving a second copy nobody can tell apart from a
    // real second edit. Callers with no replay semantics (a one-off inline
    // correction) fall back to the queue's ordered-id scheme.
    const auditId = opts.auditId || buildEventId(actorUid);

    // 1) Write the aggregate doc (existing behavior)
    await withTimeout(setDoc(doc(db, "gyms", gymId, "competitions", compId, "attempts", attemptId), {
      uid,
      routeId,
      groupId: data.groupId || "",
      sessionId: data.sessionId || "",
      ...snapshot,
      registeredBy: data.registeredBy || uid,
      updatedAt: serverTimestamp(),
      createdAt: data.createdAt || serverTimestamp(),
    }, { merge: true }), WRITE_TIMEOUT_MS, "admin attempt setDoc");

    // 2) Write audit entry (with before snapshot for admin edits)
    // Caller can override with `opts.auditType` (e.g. bulk Excel import uses
    // "admin_import" so the log distinguishes a one-by-one edit from a batch).
    await withTimeout(setDoc(
      doc(db, "gyms", gymId, "competitions", compId, "attempts", attemptId, "audit", auditId),
      {
        type: auditType,
        actorUid,
        actorRole,
        createdAt: serverTimestamp(),
        uid,
        routeId,
        groupId: data.groupId || "",
        sessionId: data.sessionId || "",
        compId,
        snapshot,
        ...(beforeSnapshot ? { beforeSnapshot } : {}),
      },
      { merge: true }
    ), WRITE_TIMEOUT_MS, "admin attempt audit setDoc");

    trackMAU(gymId, uid).catch(() => {});
  }, [gymId, authUser, competitions, sessions]);

  // Undo a competitor JAM self-save. `queueId` is the value returned by the
  // saveAttempt self-save path; `beforeData` is the aggregate snapshot (plus
  // group/session context) captured by the modal BEFORE the save it's undoing.
  //
  // Fast path: if the save is still pending in the local queue, just drop it —
  // the optimistic merge reverts to the prior server state with no Firestore
  // write at all (and if it was a first-ever save, no aggregate doc is ever
  // created). Slow path: the save already flushed, so re-save the prior
  // snapshot (idempotent, last-write-wins overwrite of the aggregate).
  const undoJamSave = useCallback(async (compId, uid, routeId, queueId, beforeData) => {
    if (!gymId) return;
    const removed = await removePendingJamSave(queueId);
    if (removed) return;
    await saveAttempt(compId, uid, routeId, {
      groupId: beforeData?.groupId || "",
      sessionId: beforeData?.sessionId || "",
      zoneAttempts: beforeData?.zoneAttempts || 0,
      zoneReached: beforeData?.zoneReached || false,
      topAttempts: beforeData?.topAttempts || 0,
      topped: beforeData?.topped || false,
    }, { actorRole: "competitor", actorUid: uid });
  }, [gymId, saveAttempt]);

  // Removed: `recomputeAndWriteAttempt` (client-side aggregate write). The
  // aggregate `attempts/{uid}_{routeId}` doc is now built ONLY by the
  // `recomputeAttemptFromAudit` Cloud Function (functions/index.js). Client
  // writes from two judges raced and overwrote each other under poor network
  // conditions; a single server-side rebuild from the audit subcollection is
  // the only safe path under concurrent recording.

  const recordJudgeEvent = useCallback(async (compId, uid, routeId, type, ctx) => {
    if (!gymId || !authUser) return;

    // Judge recording respects the attempt lock (comp-wide or per-session).
    const comp = competitions.find(c => c.id === compId);
    const session = ctx?.sessionId ? sessions.find(s => s.id === ctx.sessionId) : null;
    if (isAttemptRecordingLocked(comp, session)) {
      const err = new Error("attempts-locked");
      err.code = "attempts-locked";
      throw err;
    }

    const attemptId = `${uid}_${routeId}`;

    // Enqueue durably (IndexedDB) — survives refresh, PWA-kill, network drop.
    // The queue flushes the audit doc to Firestore; the `recomputeAttemptFromAudit`
    // CF (functions/index.js) then rebuilds the aggregate `attempts/{id}` doc
    // server-side. This is the ONLY safe path under concurrent judges: client
    // aggregate writes race with each other when two judges write at the same
    // time; a single server-side rebuild does not.
    const auditId = await enqueueJudgeEvent({
      gymId,
      compId,
      attemptId,
      type,
      actorUid: authUser.uid,
      actorRole: "judge",
      uid,
      routeId,
      groupId: ctx.groupId || "",
      sessionId: ctx.sessionId || "",
    });

    trackMAU(gymId, authUser.uid).catch(() => {});
    // Also credit the climber as MAU. Guests (synthetic uids that aren't gym
    // members) are rejected by the firestore rule, so they don't count.
    if (uid && uid !== authUser.uid) trackMAU(gymId, uid).catch(() => {});
    return auditId;
  }, [gymId, authUser, competitions, sessions]);

  const undoJudgeEvent = useCallback(async (compId, uid, routeId, auditId, _ctx) => {
    if (!gymId || !authUser) return;
    const attemptId = `${uid}_${routeId}`;

    // Fast path: if the original event is still in the local queue (not yet
    // flushed to Firestore), just drop it from the queue. No server write
    // needed — nobody else has seen it yet.
    const removedLocally = await removePendingEvent(auditId);
    if (removedLocally) return;

    // Slow path: the event was already flushed. Enqueue a revoke; it merges
    // `{ revoked: true }` onto the existing audit doc. The CF
    // `recomputeAttemptFromAudit` re-runs and excludes the revoked event from
    // the aggregate.
    await enqueueJudgeRevoke({
      gymId,
      compId,
      attemptId,
      auditId,
      actorUid: authUser.uid,
    });
  }, [gymId, authUser]);

  return { saveAttempt, undoJamSave, recordJudgeEvent, undoJudgeEvent };
}
